import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { fetchCompetitorProfile, patchCompetitor, type CompetitorProfile as Profile } from '../lib/api'
import { Card, StatCard, SectionTitle, EmptyState, TierBadge } from '../components/ui'
import { aud, metalLabel } from '../lib/format'
import { assignCategoryColors } from '../lib/palette'

export default function CompetitorProfile() {
  const { id } = useParams()
  const [data, setData] = useState<Profile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!id) return
    fetchCompetitorProfile(Number(id))
      .then((d) => { setData(d); setNotes(d.competitor.positioning_notes ?? '') })
      .catch((e) => setError(String(e)))
  }, [id])

  const mixColors = useMemo(() => assignCategoryColors((data?.metal_mix ?? []).map((m) => m.metal)), [data])

  if (error) return <EmptyState title="Couldn't load competitor" hint={error} />
  if (!data) return <p className="font-body text-sm text-ink/40">Loading…</p>

  const { competitor: c, aggregates: a, metal_mix } = data
  const poaPct = a.variant_count ? Math.round((a.poa_count / a.variant_count) * 100) : 0

  const saveNotes = async () => {
    await patchCompetitor(c.id, { positioning_notes: notes })
    setSaved(true); setTimeout(() => setSaved(false), 1800)
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/competitors" className="font-body text-xs text-ink/40 hover:text-oxblood">← All competitors</Link>
        <div className="mt-1 flex items-center gap-3">
          <h1 className="font-display text-3xl text-oxblood">{c.name}</h1>
          <TierBadge tier={c.positioning_tier} />
        </div>
        <a href={c.website_url} target="_blank" rel="noreferrer" className="font-body text-sm text-blue-700 hover:underline">
          {c.website_url}
        </a>
        {c.location && <span className="ml-2 font-body text-sm text-ink/40">· {c.location}</span>}
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label="Products" value={a.product_count} />
        <StatCard label="Variants" value={a.variant_count} />
        <StatCard label="Price range" value={a.min_price != null ? aud(a.min_price, { round: true }) : '—'} sub={a.max_price != null ? `to ${aud(a.max_price, { round: true })}` : 'no priced variants'} />
        <StatCard label="POA / quote" value={`${poaPct}%`} sub={`${a.poa_count} of ${a.variant_count}`} />
      </div>

      <section>
        <SectionTitle note="variant count by metal">Metal mix</SectionTitle>
        <Card className="p-4">
          {metal_mix.length ? (
            <ResponsiveContainer width="100%" height={Math.max(160, metal_mix.length * 34)}>
              <BarChart layout="vertical" data={metal_mix} margin={{ top: 4, right: 40, bottom: 4, left: 8 }}>
                <CartesianGrid stroke="#e1e0d9" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#898781', fontSize: 12 }} stroke="#c3c2b7" allowDecimals={false} />
                <YAxis type="category" dataKey="metal" width={120} tick={{ fill: '#0b0b0b', fontSize: 12 }} stroke="#c3c2b7" tickFormatter={metalLabel} />
                <Tooltip formatter={(v: number) => [v, 'variants']} labelFormatter={metalLabel} />
                <Bar dataKey="n" radius={[0, 4, 4, 0]} barSize={18}>
                  {metal_mix.map((m) => <Cell key={m.metal} fill={mixColors.get(m.metal)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No variant data yet" hint="Run a scrape for this competitor from the Admin screen." />}
        </Card>
      </section>

      <section>
        <SectionTitle note="editable — saved to the database">Positioning notes</SectionTitle>
        <Card className="p-4">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
            placeholder="Ethan's notes on this competitor's positioning, quirks, who they win/lose against…"
            className="w-full rounded-md border border-oxblood/15 bg-white px-3 py-2 font-body text-sm text-ink placeholder:text-ink/30" />
          <div className="mt-2 flex items-center gap-3">
            <button onClick={saveNotes} className="rounded-md bg-oxblood px-5 py-2 font-body text-sm text-offwhite hover:bg-oxblood/90">Save notes</button>
            {saved && <span className="font-body text-sm text-emerald-700">Saved ✓</span>}
          </div>
        </Card>
      </section>
    </div>
  )
}
